import React, { useState } from 'react';
import { Button, Space, Input, Form, Row, Col, Tag, Tooltip, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import { useMutation, useQuery } from '@tanstack/react-query';
import ThemNhomQuyen from './components/themNhomQuyen';
import SuaNhomQuyen from './components/suaNhomQuyen';
import { checkPermission } from '../../utils/checkPermission';
import { newObjectKeys } from '../../utils/utility';
import { deleteRoleRequest, getListRolesRequest } from '../../services/roleRequest';
import { TableCustomV2 } from '../../components/TableCustomV2';
import { getListPermissionsRequest } from '../../services/permissionRequest';
import { LIST_STATUS_ROLE } from '../../constants/appSelect';
import { CREATE_ROLE, DELETE_ROLE, GET_LIST_ROLES, UPDATE_ROLE } from '../../constants/apiEndPoint';
import { isEqual } from 'lodash';

const DanhSachNhomQuyen = () => {
  const [createPermissionGroupOpen, setCreatePermissionOpen] = useState(false);
  const [updatePermissionGroupOpen, setUpdatePermissionGroupOpen] = useState(false);
  const [permissionGroupInfo, setPermissionGroupInfo] = useState(null);
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataRoles,
    isPending,
    refetch: refetchDataPermissionGroups,
  } = useQuery({
    queryKey: ['dataRoles', params],
    queryFn: () => getListRolesRequest(params),
    enabled: checkPermission(GET_LIST_ROLES, 'Xem danh sách'),
  });

  const { data: dataAllPermissions } = useQuery({
    queryKey: ['dataAllPermissions'],
    queryFn: () => getListPermissionsRequest(),
  });

  const { mutate: mutateDeletePermissionGroup } = useMutation({
    mutationFn: deleteRoleRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa nhóm quyền thành công!',
        });
        refetchDataPermissionGroups();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      fixed: 'left',
    },
    {
      title: 'Nhóm quyền',
      dataIndex: 'name',
      render: (name) => <Typography.Text className="text-[#1677FF] hover:cursor-pointer">{name}</Typography.Text>,
      key: 'name',
      width: '200px',
      fixed: 'left',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: '200px',
    },
    {
      title: 'Số lượng người dùng',
      dataIndex: 'total_user',
      key: 'total_user',
      width: '120px',
    },

    {
      title: 'Cập nhật lần cuối',
      key: 'updated_by',
      width: '170px',
      render: (_, { updated_by_name, updated_at }) => (
        <Space direction="vertical">
          <Typography.Text>{updated_by_name}</Typography.Text>
          <Typography.Text>{updated_at}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_ROLE.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_ROLE.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: '100px',
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(UPDATE_ROLE, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa nhóm quyền">
              <EditOutlined
                className="text-base"
                onClick={() => {
                  setPermissionGroupInfo(record);
                  setUpdatePermissionGroupOpen(true);
                }}
              />
            </Tooltip>
          )}
          {checkPermission(DELETE_ROLE, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa nhóm quyền">
              <DeleteOutlined className="text-base" onClick={() => mutateDeletePermissionGroup({ id: record?.id })} />
            </Tooltip>
          )}
        </Space>
      ),
      width: '90px',
      fixed: 'right',
      hidden: !checkPermission(UPDATE_ROLE, 'Chỉnh sửa') && !checkPermission(DELETE_ROLE, 'Xóa'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };

    if (values?.name) {
      Object.assign(temp, { name: values?.name });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    } else {
      Object.assign(temp, { status: [0, 1] });
    }

    if (isEqual(temp, params)) {
      refetchDataPermissionGroups();
    } else {
      setParams(temp);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách nhóm quyền</h1>
        {checkPermission(CREATE_ROLE, 'Tạo mới') && (
          <Button type="primary" onClick={() => setCreatePermissionOpen(true)}>
            <PlusOutlined /> Thêm nhóm quyền
          </Button>
        )}
      </div>

      <ThemNhomQuyen
        open={createPermissionGroupOpen}
        setOpen={setCreatePermissionOpen}
        dataAllPermissions={dataAllPermissions?.data}
        refetchDataPermissionGroups={refetchDataPermissionGroups}
      />

      <SuaNhomQuyen
        open={updatePermissionGroupOpen}
        setOpen={setUpdatePermissionGroupOpen}
        dataAllPermissions={dataAllPermissions?.data}
        refetchDataPermissionGroups={refetchDataPermissionGroups}
        permissionGroupInfo={permissionGroupInfo}
        setPermissionGroupInfo={setPermissionGroupInfo}
      />

      {checkPermission(GET_LIST_ROLES, 'Xem danh sách') && (
        <>
          <Form layout="vertical" name="search-account" form={formSearch} onFinish={handleSubmitForm}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tên nhóm quyền" />} name="name">
                  <Input allowClear placeholder="Nhập tên nhóm quyền..." />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status" initialValue={-1}>
                  <CustomSelect
                    placeholder="Trạng thái"
                    options={[{ value: -1, label: 'Tất cả' }, ...LIST_STATUS_ROLE]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6} className="flex items-end">
                <Form.Item>
                  <Button type="primary" className=" " htmlType="submit">
                    <SearchOutlined /> Tìm kiếm
                  </Button>
                  {params &&
                    Object.values(newObjectKeys(params, ['name', 'status'])).some((value) => value || value === 0) && (
                      <Button
                        htmlType="button"
                        className="ml-3"
                        onClick={() => {
                          formSearch.resetFields();
                          setParams({ pageId: 1, limit: params.limit, status: [0, 1] });
                        }}
                      >
                        <DeleteOutlined />
                        Xóa lọc
                      </Button>
                    )}
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <TableCustomV2
            isLoading={isPending}
            columns={columns}
            dataSource={dataRoles?.data}
            totalItem={dataRoles?.totalItem}
            params={params}
            setParams={setParams}
          />
        </>
      )}
    </div>
  );
};

export default DanhSachNhomQuyen;
